import {
  PdfToiDadosHst,
  PdfToiIrregularidadeMarcarItem,
  PdfToiResponse,
} from '../models/pdf-toi.model';
import {
  AtividadeToiPorAtividadeOutput,
  MxlAtividadeToiOutput,
  MxlEconomiaPorCategoriasRow,
  ToiJsonDados,
} from '../models/toi-api.model';
import { ToiIrregularidadeItem } from '../models/toi-detalhe.model';
import {
  mapChecklistToi,
  parseJsonDadosToi,
  resolveDataConclusao,
  resolverDadosPorAtividade,
  respostaToiPositiva,
} from './toi-api.util';

/** Converte o retorno de /PorAtividade no modelo usado pelo layout do PDF. */
export function mapAtividadeToiToPdf(response: AtividadeToiPorAtividadeOutput): PdfToiResponse {
  const rawDados = resolverDadosPorAtividade(response);

  return {
    fonteDados: response.fonteDados ?? '',
    dadosHst: rawDados ? mapPdfToiDadosHst(rawDados, response.dadosHst) : null,
    irregularidadeMarcar: resolveIrregularidadeMarcar(response, rawDados),
    checklistToi: mapChecklistToi(response.checklistToi),
    eventosToi: response.eventosToi ?? [],
  };
}

function mapPdfToiDadosHst(
  dados: Record<string, unknown> | MxlAtividadeToiOutput,
  dadosHst?: Record<string, unknown>
): PdfToiDadosHst {
  const src = dados as Record<string, unknown>;
  const jsonDados = parseJsonDadosToi(String(src['jsonDadosToi'] ?? ''));
  const economiaMxl = src['economiaPorCategorias'] as MxlEconomiaPorCategoriasRow[] | undefined;

  return {
    idHstAtividadeToi: Number(src['idHstAtividadeToi'] ?? src['idAtividadeToi'] ?? 0),
    idAtividade: Number(src['idAtividade'] ?? 0),
    idAtividadeToiStatus: Number(src['idAtividadeToiStatus'] ?? 0),
    idChecklist: Number(src['idChecklist'] ?? 0),
    idFormulario: Number(src['idFormulario'] ?? 0),
    nrDocumento: String(src['nrDocumento'] ?? ''),
    cdModeloToi: String(src['cdModeloToi'] ?? ''),
    tpToi: String(src['tpToi'] ?? ''),
    prazoDias: resolvePrazoDiasPdf(src),
    flagGerado: Boolean(src['flagGerado']),
    flagEntregueMaos: Boolean(src['flagEntregueMaos']),
    flagRecusaRecebimento: Boolean(src['flagRecusaRecebimento']),
    flagPermiteEdicao: Boolean(src['flagPermiteEdicao']),
    flagSincronizadoMobile: Boolean(src['flagSincronizadoMobile']),
    flagExigeEnvioCorreios: Boolean(src['flagExigeEnvioCorreios']),
    flagEnderecoPendenteTratamento: Boolean(src['flagEnderecoPendenteTratamento']),
    dataGeracao: String(src['dataGeracao'] ?? ''),
    dataVistoria: String(src['dataVistoria'] ?? ''),
    dataConclusao: resolveDataConclusao(src, dadosHst),
    dataEntregaMaos: (src['dataEntregaMaos'] as string | null) ?? null,
    dataValidacao: (src['dataValidacao'] as string | null) ?? null,
    cdUsuarioValidacao: (src['cdUsuarioValidacao'] as string | null) ?? null,
    dataAprovacao: (src['dataAprovacao'] as string | null) ?? null,
    cdUsuarioAprovacao: (src['cdUsuarioAprovacao'] as string | null) ?? null,
    dataEnvioCorreios: (src['dataEnvioCorreios'] as string | null) ?? null,
    dataPostagem: (src['dataPostagem'] as string | null) ?? null,
    dataEntregaCorreios: (src['dataEntregaCorreios'] as string | null) ?? null,
    dataUltimaOcorrencia: String(src['dataUltimaOcorrencia'] ?? ''),
    dataUltimaAlteracao: String(src['dataUltimaAlteracao'] ?? ''),
    cdUsuarioUltimaAlteracao: String(src['cdUsuarioUltimaAlteracao'] ?? ''),
    cdResponsavelEquipe: String(src['cdResponsavelEquipe'] ?? ''),
    nmResponsavelEquipe: String(src['nmResponsavelEquipe'] ?? ''),
    dsGerenciaRegional: String(src['dsGerenciaRegional'] ?? ''),
    dsLocalidade: String(src['dsLocalidade'] ?? ''),
    dsEnderecoUnidade: String(src['dsEnderecoUnidade'] ?? ''),
    nrMatriculaImovel: String(src['nrMatriculaImovel'] ?? ''),
    nmUsuarioCadastrado: String(src['nmUsuarioCadastrado'] ?? ''),
    nrIdentificadorUsuario: String(src['nrIdentificadorUsuario'] ?? ''),
    numeroBa: String(src['numeroBa'] ?? ''),
    cdServico: String(src['cdServico'] ?? ''),
    dsTipoInfracao: String(src['dsTipoInfracao'] ?? ''),
    dsTipoIrregularidade: String(src['dsTipoIrregularidade'] ?? ''),
    economiaPorCategorias: resolvePdfEconomiaPorCategorias(economiaMxl, jsonDados),
    jsonDadosToi: String(src['jsonDadosToi'] ?? ''),
    jsonControleFotos: String(src['jsonControleFotos'] ?? ''),
    nrLoteCorreios: (src['nrLoteCorreios'] as string | null) ?? null,
    nrAr: (src['nrAr'] as string | null) ?? null,
    cdRastreio: (src['cdRastreio'] as string | null) ?? null,
    dsRetornoCorreios: (src['dsRetornoCorreios'] as string | null) ?? null,
    dsObservacaoTratamento: (src['dsObservacaoTratamento'] as string | null) ?? null,
    dataCadastro: String(src['dataCadastro'] ?? ''),
    usuarioCadastro: String(src['usuarioCadastro'] ?? ''),
    dataAtualizacao: (src['dataAtualizacao'] as string | null) ?? null,
    usuarioAtualizacao: (src['usuarioAtualizacao'] as string | null) ?? null,
    dataReferencia: String(src['dataReferencia'] ?? ''),
  };
}

function resolvePrazoDiasPdf(src: Record<string, unknown>): number | null {
  const raw =
    src['prazoDias'] ?? src['qtDiasPrazoRegularizacao'] ?? src['nrDiasPrazoRegularizacao'] ?? null;
  if (raw == null || raw === '') {
    return null;
  }
  const numero = Number(raw);
  return Number.isFinite(numero) ? numero : null;
}

function resolvePdfEconomiaPorCategorias(
  rows: MxlEconomiaPorCategoriasRow[] | undefined,
  jsonDados: ToiJsonDados | null
): MxlEconomiaPorCategoriasRow[] {
  if (rows?.length) {
    return rows;
  }

  return (jsonDados?.economiaPorCategorias ?? []).map((row) => ({
    tps: row.tipoLinha,
    comercial: row.comercial,
    industrial: row.industrial,
    publica: row.publica,
    residencial: row.residencial,
    social: row.social,
  }));
}

/** A lista de checkboxes a marcar pode vir na raiz, nos dados ou dentro do `jsonDadosToi`. */
function resolveIrregularidadeMarcar(
  response: AtividadeToiPorAtividadeOutput,
  rawDados?: Record<string, unknown> | MxlAtividadeToiOutput
): PdfToiIrregularidadeMarcarItem[] {
  const responseRecord = response as AtividadeToiPorAtividadeOutput & Record<string, unknown>;
  const dadosRecord = rawDados as Record<string, unknown> | undefined;
  const jsonDados = parseJsonDadosToi(String(dadosRecord?.['jsonDadosToi'] ?? ''));

  const sources: unknown[] = [
    response.irregularidadeMarcar,
    responseRecord['IrregularidadeMarcar'],
    dadosRecord?.['irregularidadeMarcar'],
    dadosRecord?.['IrregularidadeMarcar'],
    jsonDados?.irregularidadeMarcar,
    jsonDados?.['IrregularidadeMarcar'],
  ];

  for (const source of sources) {
    const normalizado = normalizarIrregularidadeMarcar(source as unknown[] | undefined);
    if (normalizado.length) {
      return normalizado;
    }
  }

  return montarIrregularidadeMarcarDeLista(jsonDados?.irregularidades);
}

function normalizarIrregularidadeMarcar(
  items: unknown[] | undefined | null
): PdfToiIrregularidadeMarcarItem[] {
  if (!items?.length) {
    return [];
  }

  return items
    .map((item) => {
      if (typeof item === 'string') {
        return { texto: item.trim() };
      }
      const row = item as Record<string, unknown>;
      return { texto: String(row['texto'] ?? row['Texto'] ?? '').trim() };
    })
    .filter((item) => item.texto);
}

/** Fallback: deriva os checkboxes da lista de irregularidades respondidas com "SIM". */
function montarIrregularidadeMarcarDeLista(
  irregularidades: ToiIrregularidadeItem[] | undefined
): PdfToiIrregularidadeMarcarItem[] {
  if (!irregularidades?.length) {
    return [];
  }

  return irregularidades
    .filter((item) => respostaToiPositiva(item.resposta))
    .flatMap((item) => {
      const row = item as ToiIrregularidadeItem & { Texto?: string; Chave?: string };
      const chaves = [row.texto, row.chave, row.Texto, row.Chave].filter(Boolean) as string[];
      return chaves.map((texto) => ({ texto }));
    });
}
